import { useState } from 'react'
import {
  Box,
  Button,
  Typography,
  Alert,
  TextField,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme
} from '@mui/material'
import {
  Security,
  QrCode2,
  CheckCircle,
  ContentCopy,
  Warning
} from '@mui/icons-material'
import { mfaService } from '../../../../shared/services/MfaService'
import { useAuth } from '../../../../shared/auth/AuthContext'

export function MfaSetupComponent() {
  const theme = useTheme()
  const { user, refetchUser } = useAuth()
  const [activeStep, setActiveStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Setup state
  const [secret, setSecret] = useState<string>('')
  const [qrCodeUri, setQrCodeUri] = useState<string>('')
  const [verificationCode, setVerificationCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])

  // Disable state
  const [showDisableDialog, setShowDisableDialog] = useState(false)
  const [disablePassword, setDisablePassword] = useState('')

  const [copiedSecret, setCopiedSecret] = useState(false)
  const [copiedRecoveryCodes, setCopiedRecoveryCodes] = useState(false)

  const mfaEnabled = user?.mfaEnabled || false

  const handleStartSetup = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await mfaService.initiateEnrollment()
      setSecret(response.secret)
      setQrCodeUri(response.qrCodeDataUri)
      setActiveStep(1)
    } catch (err: any) {
      setError(err.message || 'Failed to initialize MFA setup')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code')
      return
    }

    setLoading(true)
    setError(null)
    try {
      const response = await mfaService.confirmEnrollment(verificationCode)
      setRecoveryCodes(response.recoveryCodes)
      setActiveStep(2)
      setSuccess('MFA successfully enabled!')
      await refetchUser() // Update user state to reflect MFA is now enabled
    } catch (err: any) {
      setError(err.message || 'Invalid verification code. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleDisableMfa = async () => {
    if (!disablePassword) {
      setError('Please enter your password')
      return
    }

    setLoading(true)
    setError(null)
    try {
      await mfaService.disable(disablePassword)
      setShowDisableDialog(false)
      setDisablePassword('')
      setSuccess('MFA has been disabled')
      await refetchUser()
    } catch (err: any) {
      setError(err.message || 'Failed to disable MFA. Check your password.')
    } finally {
      setLoading(false)
    }
  }

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret)
      setCopiedSecret(true)
      setTimeout(() => setCopiedSecret(false), 2000)
    } catch (err) {
      console.error('Failed to copy secret')
    }
  }

  const handleCopyRecoveryCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'))
      setCopiedRecoveryCodes(true)
      setTimeout(() => setCopiedRecoveryCodes(false), 2000)
    } catch (err) {
      console.error('Failed to copy recovery codes')
    }
  }

  const handleReset = () => {
    setActiveStep(0)
    setSecret('')
    setQrCodeUri('')
    setVerificationCode('')
    setRecoveryCodes([])
    setError(null)
    setSuccess(null)
  }

  if (mfaEnabled) {
    return (
      <Card sx={{ background: alpha(theme.palette.success.main, 0.05), borderLeft: `4px solid ${theme.palette.success.main}` }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CheckCircle sx={{ fontSize: 40, color: theme.palette.success.main }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Multi-Factor Authentication Enabled
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your account is protected with MFA
              </Typography>
            </Box>
          </Box>

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            MFA is enabled and working. You'll need to enter a code from your authenticator app each time you log in.
          </Alert>

          <Button
            variant="outlined"
            color="error"
            startIcon={<Warning />}
            onClick={() => setShowDisableDialog(true)}
          >
            Disable MFA
          </Button>

          {/* Disable MFA Dialog */}
          <Dialog open={showDisableDialog} onClose={() => setShowDisableDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Disable Multi-Factor Authentication
            </DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Warning:</strong> Disabling MFA will make your account less secure. As an administrator, you are required to have MFA enabled.
              </Alert>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter your password to confirm:
              </Typography>
              <TextField
                fullWidth
                type="password"
                label="Password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                autoFocus
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowDisableDialog(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDisableMfa}
                disabled={loading || !disablePassword}
              >
                {loading ? <CircularProgress size={20} /> : 'Disable MFA'}
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Security sx={{ fontSize: 32, color: theme.palette.primary.main }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Multi-Factor Authentication Setup
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add an extra layer of security to your account
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel>Start MFA Setup</StepLabel>
            <StepContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Multi-factor authentication adds an extra layer of security by requiring a time-based code from your mobile device in addition to your password.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                You'll need an authenticator app like <strong>Google Authenticator</strong>, <strong>Microsoft Authenticator</strong>, or <strong>Authy</strong> installed on your mobile device.
              </Alert>
              <Button
                variant="contained"
                onClick={handleStartSetup}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <QrCode2 />}
              >
                {loading ? 'Generating...' : 'Begin Setup'}
              </Button>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Scan QR Code</StepLabel>
            <StepContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Open your authenticator app and scan this QR code:
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                {qrCodeUri && (
                  <img
                    src={qrCodeUri}
                    alt="QR Code"
                    style={{
                      maxWidth: '250px',
                      border: `2px solid ${theme.palette.divider}`,
                      borderRadius: '8px',
                      padding: '16px',
                      background: 'white'
                    }}
                  />
                )}
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Can't scan the QR code?</strong> Enter this secret key manually:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <code style={{ fontFamily: 'monospace', fontSize: '14px', padding: '8px', background: alpha(theme.palette.primary.main, 0.1), borderRadius: '4px', flex: 1 }}>
                    {secret}
                  </code>
                  <Button
                    size="small"
                    startIcon={copiedSecret ? <CheckCircle /> : <ContentCopy />}
                    onClick={handleCopySecret}
                    color={copiedSecret ? 'success' : 'primary'}
                  >
                    {copiedSecret ? 'Copied!' : 'Copy'}
                  </Button>
                </Box>
              </Alert>

              <TextField
                fullWidth
                label="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                sx={{ mb: 2 }}
                helperText="Enter the 6-digit code from your authenticator app"
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button onClick={handleReset} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleVerifyCode}
                  disabled={loading || verificationCode.length !== 6}
                  startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                >
                  {loading ? 'Verifying...' : 'Verify & Enable MFA'}
                </Button>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Save Recovery Codes</StepLabel>
            <StepContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Important:</strong> Save these recovery codes in a safe place. You can use them to access your account if you lose your authenticator device.
              </Alert>

              <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), p: 2, borderRadius: 1, mb: 2 }}>
                <Grid container spacing={1}>
                  {recoveryCodes.map((code, index) => (
                    <Grid item xs={6} key={index}>
                      <code style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                        {code}
                      </code>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Button
                variant="outlined"
                startIcon={copiedRecoveryCodes ? <CheckCircle /> : <ContentCopy />}
                onClick={handleCopyRecoveryCodes}
                color={copiedRecoveryCodes ? 'success' : 'primary'}
                sx={{ mb: 2 }}
                fullWidth
              >
                {copiedRecoveryCodes ? 'Codes Copied!' : 'Copy All Recovery Codes'}
              </Button>

              <Alert severity="success" icon={<CheckCircle />}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  MFA is now enabled and protecting your account!
                </Typography>
              </Alert>

              <Button
                variant="contained"
                onClick={handleReset}
                sx={{ mt: 2 }}
                fullWidth
              >
                Done
              </Button>
            </StepContent>
          </Step>
        </Stepper>
      </CardContent>
    </Card>
  )
}
